import { useCallback, useContext, useEffect, useState } from 'react';
import { AppContext } from '@layouts/LayoutAdmin/LayoutAdmin';
import { layoutApi } from '@services/layoutApi';
import { inventoryProgressApi } from '@services/inventoryProgressApi';
import { inventorySessionApi } from '@services/inventorySessionApi';
import { appToast } from '@utils/appToast';
import {
  ALL_COMPANIES_OPTION,
  ALL_STORES_OPTION,
  ALL_VALUE,
  PROGRESS_REVIEW_CONSTANT,
  TCompanyOption,
  TProgressRow,
  TProgressTotalRow,
  TStoreOption,
  TYearMonthOption,
} from '../constant';

const { MESSAGE } = PROGRESS_REVIEW_CONSTANT;

// [4.10] 部門 — antd Table needs a unique rowKey across parent (store) and child (department)
// rows; BE leaves 会社/店舗 blank on department rows, so keys are derived from the parent store.
function assignRowKeys(rows: TProgressRow[]): TProgressRow[] {
  return rows.map((row) => {
    const parentKey = `${row.companyCode ?? ''}-${row.storeCode ?? ''}`;
    return {
      ...row,
      key: parentKey,
      departments: (row.departments ?? []).map((dept, index) => ({
        ...dept,
        key: `${parentKey}-dept-${dept.departmentCode ?? index}`,
      })),
    } as TProgressRow;
  });
}

function useProgressReviewPage() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { role } = useContext(AppContext);

  // ── Live filter values shown in dropdowns ──────────────────────────────────
  // UTC-TC012 (F2, round 2 — quyết định BA 2026-08-03): 会社 mặc định khi mở màn hình lần đầu là
  // "công ty đầu tiên" trong danh sách (KHÔNG phải "全て"/ALL_VALUE). Danh sách 年月/会社 này giờ lấy
  // từ DB qua inventorySessionApi.getSessions()/layoutApi.getCompanies() (xem fetch* bên dưới), nên
  // giá trị mặc định chỉ biết được sau khi API trả về — bắt đầu rỗng, set trong effect khởi tạo.
  const [yearMonth, setYearMonth] = useState<string>('');
  const [companyCode, setCompanyCode] = useState<string>('');
  const [storeCode, setStoreCode] = useState<string>('');

  // ── Year-month/Company/Store options — loaded from DB (same endpoints as VAMS_PC_004) ───────
  const [yearMonthOptions, setYearMonthOptions] = useState<TYearMonthOption[]>(
    []
  );
  const [companyOptions, setCompanyOptions] = useState<TCompanyOption[]>([
    ALL_COMPANIES_OPTION,
  ]);
  const [storeOptions, setStoreOptions] = useState<TStoreOption[]>([]);

  // ── Loading ───────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);

  // ── API data ──────────────────────────────────────────────────────────────
  // Rows and total row from last successful GET /api/v1/inventory/progress call.
  const [apiRows, setApiRows] = useState<TProgressRow[]>([]);
  const [apiTotalRow, setApiTotalRow] = useState<TProgressTotalRow | null>(
    null
  );

  // [3.3] disabled when company = "全て" (ALL_VALUE) per spec VAL-002.
  // F2 (round 2): vì companyCode mặc định giờ là công ty đầu tiên trả về từ DB (không phải
  //   ALL_VALUE), isStoreDisabled = false NGAY TỪ ĐẦU khi mount (店舗 enabled từ đầu). Đây là hệ
  //   quả tất yếu của quyết định BA cho F2 — xem bug-fix.md F2 round 2 về ảnh hưởng ngược tới UTC-TC019.
  const isStoreDisabled = companyCode === ALL_VALUE || companyCode === '';

  // [3.1] 年月 — GET /api/v1/inventory/sessions (inventorySessionApi, dùng chung với VAMS_PC_004).
  // Dedupe theo inventoryYearMonth, sort desc; "Default: most recent open inventory period" (spec)
  // → phần tử đầu tiên sau khi sort desc.
  const fetchYearMonths = useCallback(async (): Promise<string> => {
    try {
      const response = await inventorySessionApi.getSessions();
      if (response.ok && response.data.length > 0) {
        const uniqueYearMonths = Array.from(
          new Set(
            response.data
              .map((item) => item.inventoryYearMonth)
              .filter((ym): ym is string => Boolean(ym))
          )
        ).sort((a, b) => b.localeCompare(a));

        setYearMonthOptions(
          uniqueYearMonths.map((ym) => ({
            value: ym,
            label: `${ym.slice(0, 4)}/${ym.slice(4)}`,
          }))
        );
        return uniqueYearMonths[0];
      }
      setYearMonthOptions([]);
    } catch {
      setYearMonthOptions([]);
    }
    return '';
  }, []);

  // [3.2] 会社 — GET api/v1/company (layoutApi, dùng chung với VAMS_PC_004).
  // UTC-TC013: danh sách phải tăng dần theo alphabet sau "全て".
  // Trả về companyCode đầu tiên (sau khi sort) để làm giá trị mặc định (UTC-TC012).
  const fetchCompanies = useCallback(async (): Promise<string> => {
    try {
      const response = await layoutApi.getCompanies();
      if (response.ok && response.data.length > 0) {
        const sorted = [...response.data].sort((a, b) =>
          a.companyCode.localeCompare(b.companyCode)
        );
        setCompanyOptions([
          ALL_COMPANIES_OPTION,
          ...sorted.map((c) => ({
            value: c.companyCode,
            label: `${c.companyCode}_${c.companyName}`,
          })),
        ]);
        return sorted[0].companyCode;
      }
      setCompanyOptions([ALL_COMPANIES_OPTION]);
    } catch {
      setCompanyOptions([ALL_COMPANIES_OPTION]);
    }
    return ALL_VALUE;
  }, []);

  // [3.3] 店舗 — GET api/v1/store?CompanyCode=... (layoutApi, dùng chung với VAMS_PC_004).
  // Gọi lại mỗi khi 会社 [3.2] đổi; company="全て" → store rỗng + disabled (VAL-002).
  const fetchStores = useCallback(async (targetCompanyCode: string) => {
    if (!targetCompanyCode || targetCompanyCode === ALL_VALUE) {
      setStoreOptions([]);
      return;
    }
    try {
      const response = await layoutApi.getStoresByCompany({
        companyCode: targetCompanyCode,
        companyName: '',
      });
      if (response.ok) {
        const sorted = [...response.data].sort((a, b) =>
          a.storeCode.localeCompare(b.storeCode)
        );
        setStoreOptions([
          ALL_STORES_OPTION,
          ...sorted.map((s) => ({
            value: s.storeCode,
            label: `${s.storeCode}_${s.storeName}`,
            companyCode: targetCompanyCode,
          })),
        ]);
      } else {
        setStoreOptions([]);
      }
    } catch {
      setStoreOptions([]);
    }
  }, []);

  // ── Fetch progress data from API ──────────────────────────────────────────
  const fetchProgress = useCallback(
    async (params: {
      yearMonth: string;
      companyCode: string;
      storeCode: string;
    }) => {
      setLoading(true);
      try {
        const response = await inventoryProgressApi.getProgress({
          inventoryYearMonth: params.yearMonth,
          companyCode: params.companyCode,
          storeCode: params.storeCode || undefined,
        });

        if (response.ok && response.data) {
          const data = response.data;
          // Shapes of TInventoryProgressRow (model) and TProgressRow (constant.ts) are identical
          // per api-integration.md §5.2 — cast is safe.
          setApiRows(
            assignRowKeys((data.rows ?? []) as unknown as TProgressRow[])
          );
          const tr = data.totalRow;
          setApiTotalRow(
            tr
              ? {
                  totalCount: tr.totalCount,
                  confirmedCount: tr.confirmedCount,
                  confirmedRate: tr.confirmedRate,
                  confirmedRateDisplay: tr.confirmedRateDisplay,
                  unconfirmedCount: tr.unconfirmedCount,
                  unusedCount: tr.unusedCount,
                  unlistedCount: tr.unlistedCount,
                  moveCount: tr.moveCount,
                  removeCount: tr.removeCount,
                }
              : null
          );
        } else {
          // Structured error (e.g. INVENTORY_SESSION_NOT_FOUND) — show empty table + error toast
          setApiRows([]);
          setApiTotalRow(null);
          appToast.error({ message: MESSAGE.E01 });
        }
      } catch {
        // MSG-PC009-E01 (UTC-TC024): network failure / 5xx must show the
        // screen's own "データの取得に失敗しました。再度お試しください。" message,
        // not the generic COMMON_MESSAGE_ERROR toast.
        setApiRows([]);
        setApiTotalRow(null);
        appToast.error({ message: MESSAGE.E01 });
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ── Initial load on mount ──────────────────────────────────────────────────
  // F2 (round 2 — quyết định BA): 年月/会社/店舗 giờ lấy từ DB — phải resolve yearMonth + companyCode
  //   mặc định (fetchYearMonths/fetchCompanies, chạy song song vì độc lập) rồi mới load store list +
  //   gọi progress API đầu tiên, để request nhất quán với dropdown hiển thị.
  useEffect(() => {
    (async () => {
      const [defaultYearMonth, defaultCompanyCode] = await Promise.all([
        fetchYearMonths(),
        fetchCompanies(),
      ]);
      setYearMonth(defaultYearMonth);
      setCompanyCode(defaultCompanyCode);
      await fetchStores(defaultCompanyCode);
      await fetchProgress({
        yearMonth: defaultYearMonth,
        companyCode: defaultCompanyCode,
        storeCode: '',
      });
    })();
    // run once on mount — fetch* callbacks are stable (useCallback, empty deps)
  }, []); // eslint-disable-line

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChangeYearMonth = useCallback((value: string) => {
    setYearMonth(value);
  }, []);

  const handleChangeCompany = useCallback(
    (value: string) => {
      setCompanyCode(value);
      // Reset store when company changes (spec [3.2]: "[3.3]をblankにリセット")
      setStoreCode('');
      fetchStores(value);
    },
    [fetchStores]
  );

  const handleChangeStore = useCallback((value: string) => {
    setStoreCode(value);
  }, []);

  // [3.4] Refresh — send current filters to API (spec: "🔄クリックで反映")
  const handleRefresh = useCallback(async () => {
    await fetchProgress({ yearMonth, companyCode, storeCode });
  }, [fetchProgress, yearMonth, companyCode, storeCode]);

  return {
    state: {
      yearMonth,
      companyCode,
      storeCode,
      yearMonthOptions,
      companyOptions,
      storeOptions,
      isStoreDisabled,
      displayRows: apiRows,
      totalRow: apiTotalRow,
      loading,
    },
    handler: {
      handleChangeYearMonth,
      handleChangeCompany,
      handleChangeStore,
      handleRefresh,
    },
  };
}

export default useProgressReviewPage;
